import React, { useState, useEffect, useRef } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, 
  faSpinner, 
  faSearch,
  faTimes,
  faCheck,
  faFileExcel,
  faFileCsv
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';

const UploadInterviewFile = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    file: null,
    survey_id: '',
    interviewer_ids: []
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Survey selection state
  const [surveys, setSurveys] = useState([]);
  const [surveySearch, setSurveySearch] = useState('');
  const [showSurveyDropdown, setShowSurveyDropdown] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const surveyDropdownRef = useRef(null);

  // Interviewer selection state (multi-select)
  const [interviewers, setInterviewers] = useState([]);
  const [interviewerSearch, setInterviewerSearch] = useState('');
  const [showInterviewerDropdown, setShowInterviewerDropdown] = useState(false);
  const [selectedInterviewers, setSelectedInterviewers] = useState([]);
  const interviewerDropdownRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showBlacklistResetModal, setShowBlacklistResetModal] = useState(false);
  const showBlacklistResetModalDismiss = useModalDismiss(() => setShowBlacklistResetModal(false), { backdropClose: false, enabled: showBlacklistResetModal });
  const [resettingBlacklist, setResettingBlacklist] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (surveyDropdownRef.current && !surveyDropdownRef.current.contains(event.target)) {
        setShowSurveyDropdown(false);
      }
      if (interviewerDropdownRef.current && !interviewerDropdownRef.current.contains(event.target)) {
        setShowInterviewerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoadingData(true);
      const [surveysResponse, interviewersResponse] = await Promise.all([
        api.get('/cati/surveys'),
        api.get('/cati/interviewers')
      ]);

      if (surveysResponse.data.success) {
        setSurveys(surveysResponse.data.data || []);
      }

      if (interviewersResponse.data.success) {
        setInterviewers(interviewersResponse.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrors({ general: 'Erreur lors du chargement des données' });
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateAndSetFile = (file) => {
    if (!file) return false;

    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      setErrors(prev => ({
        ...prev,
        file: 'Format de fichier non supporté. Veuillez sélectionner un fichier Excel (.xlsx, .xls) ou CSV.'
      }));
      return false;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      setErrors(prev => ({
        ...prev,
        file: 'Le fichier est trop volumineux. Taille maximale: 10MB'
      }));
      return false;
    }

    setFormData(prev => ({
      ...prev,
      file: file
    }));
    
    if (errors.file) {
      setErrors(prev => ({
        ...prev,
        file: ''
      }));
    }
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    validateAndSetFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  const handleSurveySearch = (value) => {
    setSurveySearch(value);
    setShowSurveyDropdown(true);
  };

  const handleSurveySelect = (survey) => {
    setSelectedSurvey(survey);
    setSurveySearch(survey.title);
    setFormData(prev => ({
      ...prev,
      survey_id: survey.id
    }));
    setShowSurveyDropdown(false);
  };

  const removeSelectedSurvey = () => {
    setSelectedSurvey(null);
    setSurveySearch('');
    setFormData(prev => ({
      ...prev,
      survey_id: ''
    }));
  };

  const handleInterviewerSearch = (value) => {
    setInterviewerSearch(value);
    setShowInterviewerDropdown(true);
  };

  const handleInterviewerSelect = (interviewer) => {
    // Check if interviewer is already selected
    if (selectedInterviewers.find(i => i.id === interviewer.id)) {
      return; // Already selected, do nothing
    }
    
    // Add to selected list
    const newSelected = [...selectedInterviewers, interviewer];
    setSelectedInterviewers(newSelected);
    setInterviewerSearch('');
    setFormData(prev => ({
      ...prev,
      interviewer_ids: newSelected.map(i => i.id)
    }));
    setShowInterviewerDropdown(false);
  };

  const removeSelectedInterviewer = (interviewerId) => {
    const newSelected = selectedInterviewers.filter(i => i.id !== interviewerId);
    setSelectedInterviewers(newSelected);
    setFormData(prev => ({
      ...prev,
      interviewer_ids: newSelected.map(i => i.id)
    }));
  };

  const filteredSurveys = surveys.filter(survey => {
    const matchesSearch = survey.title.toLowerCase().includes(surveySearch.toLowerCase()) ||
                         survey.limesurvey_id?.toString().includes(surveySearch);
    return matchesSearch && survey.is_active;
  });

  const filteredInterviewers = interviewers.filter(interviewer => {
    const matchesSearch = interviewer.name.toLowerCase().includes(interviewerSearch.toLowerCase()) ||
                         interviewer.email.toLowerCase().includes(interviewerSearch.toLowerCase());
    const isCati = interviewer.is_cati;
    const notAlreadySelected = !selectedInterviewers.find(i => i.id === interviewer.id);
    return matchesSearch && isCati && notAlreadySelected;
  });

  const validateForm = () => {
    const newErrors = {};

    if (!formData.file) {
      newErrors.file = 'Le fichier est requis';
    }

    if (!formData.survey_id) {
      newErrors.survey_id = 'L\'enquête est requise';
    }

    if (!formData.interviewer_ids || formData.interviewer_ids.length === 0) {
      newErrors.interviewer_ids = 'Au moins un enquêteur est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', formData.file);
      uploadFormData.append('survey_id', formData.survey_id);
      formData.interviewer_ids.forEach(id => {
        uploadFormData.append('interviewer_ids[]', id);
      });

      const response = await api.post('/cati/interview-uploads', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setShowBlacklistResetModal(true);
      } else {
        setErrors({ general: response.data.message || 'Erreur lors du téléchargement du fichier' });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.response?.data?.message || 'Erreur lors du téléchargement du fichier' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBlacklistReset = async () => {
    setResettingBlacklist(true);
    try {
      await api.post('/cati/blacklist/reset-all');
      setSuccessMessage('Fichier importé et blacklist réinitialisée avec succès');
    } catch (error) {
      console.error('Error resetting blacklist:', error);
      setSuccessMessage('Fichier importé avec succès (erreur lors de la réinitialisation de la blacklist)');
    } finally {
      setResettingBlacklist(false);
      setShowBlacklistResetModal(false);
      setShowSuccessModal(true);
      setTimeout(() => navigate('/cati/interview-uploads'), 2000);
    }
  };

  const handleSkipBlacklistReset = () => {
    setShowBlacklistResetModal(false);
    setSuccessMessage('Fichier importé avec succès');
    setShowSuccessModal(true);
    setTimeout(() => navigate('/cati/interview-uploads'), 2000);
  };

  const handleCancel = () => {
    navigate('/cati/interview-uploads');
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">TÉLÉCHARGER FICHIER D'INTERVIEWS</h1>
            <p className="text-gray-600">Télécharger un fichier Excel ou CSV contenant les informations des personnes à interviewer</p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* General Error */}
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{errors.general}</p>
                </div>
              )}

              {/* File Upload */}
              <div>
                <label htmlFor="file" className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier (Excel ou CSV) *
                </label>
                <div 
                  className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors ${
                    isDragging 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-300 hover:border-red-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="space-y-1 text-center">
                    {formData.file ? (
                      <div className="flex flex-col items-center">
                        <FontAwesomeIcon 
                          icon={formData.file.name.match(/\.csv$/i) ? faFileCsv : faFileExcel} 
                          className="text-4xl text-green-600 mb-2" 
                        />
                        <p className="text-sm text-gray-900 font-medium">{formData.file.name}</p>
                        <p className="text-xs text-gray-500">
                          {(formData.file.size / 1024).toFixed(2)} KB
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, file: null }));
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                          className="mt-2 text-sm text-red-600 hover:text-red-800"
                        >
                          <FontAwesomeIcon icon={faTimes} className="mr-1" />
                          Supprimer
                        </button>
                      </div>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faUpload} className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="file"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-red-600 hover:text-red-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-red-500"
                          >
                            <span>Télécharger un fichier</span>
                            <input
                              id="file"
                              name="file"
                              type="file"
                              accept=".xlsx,.xls,.csv"
                              className="sr-only"
                              ref={fileInputRef}
                              onChange={handleFileChange}
                            />
                          </label>
                          <p className="pl-1">ou glissez-déposez</p>
                        </div>
                        <p className="text-xs text-gray-500">XLSX, XLS, CSV jusqu'à 10MB</p>
                      </>
                    )}
                  </div>
                </div>
                {errors.file && (
                  <p className="mt-1 text-sm text-red-600">{errors.file}</p>
                )}
              </div>

              {/* Survey Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enquête *
                </label>
                <div className="relative" ref={surveyDropdownRef}>
                  <div className="relative">
                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher une enquête..."
                      value={surveySearch}
                      onChange={(e) => handleSurveySearch(e.target.value)}
                      onFocus={() => setShowSurveyDropdown(true)}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.survey_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>

                  {/* Selected Survey Display */}
                  {selectedSurvey && (
                    <div className="mt-2 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-2" />
                        <span className="text-green-800 font-medium">{selectedSurvey.title}</span>
                        {selectedSurvey.limesurvey_id && (
                          <span className="text-green-600 ml-2">(ID: {selectedSurvey.limesurvey_id})</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={removeSelectedSurvey}
                        className="text-green-600 hover:text-green-800"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}

                  {/* Survey Dropdown */}
                  {showSurveyDropdown && surveySearch && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredSurveys.length > 0 ? (
                        filteredSurveys.map(survey => (
                          <div
                            key={survey.id}
                            onClick={() => handleSurveySelect(survey)}
                            className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <div className="font-medium text-gray-900">{survey.title}</div>
                            {survey.limesurvey_id && (
                              <div className="text-sm text-gray-500">ID: {survey.limesurvey_id}</div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">Aucune enquête trouvée</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.survey_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.survey_id}</p>
                )}
              </div>

              {/* Interviewer Selection (Multi-select) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enquêteurs CATI * (au moins un)
                </label>
                <div className="relative" ref={interviewerDropdownRef}>
                  <div className="relative">
                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher un enquêteur..."
                      value={interviewerSearch}
                      onChange={(e) => handleInterviewerSearch(e.target.value)}
                      onFocus={() => setShowInterviewerDropdown(true)}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.interviewer_ids ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>

                  {/* Selected Interviewers Display */}
                  {selectedInterviewers.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedInterviewers.map(interviewer => (
                        <div
                          key={interviewer.id}
                          className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2"
                        >
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-2" />
                            <span className="text-green-800 font-medium text-sm">{interviewer.name}</span>
                            <span className="text-green-600 ml-2 text-xs">({interviewer.email})</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeSelectedInterviewer(interviewer.id)}
                            className="text-green-600 hover:text-green-800 ml-2"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Interviewer Dropdown */}
                  {showInterviewerDropdown && interviewerSearch && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredInterviewers.length > 0 ? (
                        filteredInterviewers.map(interviewer => (
                          <div
                            key={interviewer.id}
                            onClick={() => handleInterviewerSelect(interviewer)}
                            className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <div className="font-medium text-gray-900">{interviewer.name}</div>
                            <div className="text-sm text-gray-500">{interviewer.email}</div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">Aucun enquêteur trouvé</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.interviewer_ids && (
                  <p className="mt-1 text-sm text-red-600">{errors.interviewer_ids}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 sm:gap-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Téléchargement...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faUpload} className="mr-2" />
                      Télécharger le fichier
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Blacklist Reset Modal */}
      {showBlacklistResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Fichier importé avec succès</h3>
                  <p className="text-sm text-gray-600">Souhaitez-vous réinitialiser la blacklist des agences ?</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4">
                Cette action remettra toutes les agences à <strong>0/3</strong> et débloquera celles qui sont actuellement blacklistées.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleSkipBlacklistReset}
                  disabled={resettingBlacklist}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Non
                </button>
                <button
                  onClick={handleBlacklistReset}
                  disabled={resettingBlacklist}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors"
                >
                  {resettingBlacklist ? (
                    <><FontAwesomeIcon icon={faSpinner} spin className="mr-2" />Réinitialisation...</>
                  ) : (
                    'Oui, réinitialiser'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />
    </div>
  );
};

export default UploadInterviewFile;

